<script lang="ts">
  import { isBillingSettlementPending } from "@/server-state-policy";
  import type { GameServer } from "@/api";
  import { routeHref } from "@/router";
  import { gameLabel, statusLabels, serverStatusHint, transitionalStatuses } from "@/config";
  import { user } from "@/stores/session";
  import {
    balance,
    serverLimit,
    servers,
    busyAction,
    dailyBonusClaimedToday,
    claimDailyBonus,
    runServerAction,
  } from "@/stores/dashboard";
  import { getGreeting, formatNumber } from "@/utils/format";
  import { runningServerSummary } from "@/utils/running-server-summary";

  const runningSummary = $derived(runningServerSummary($servers, $balance));

  function handleClaimBonus() {
    void claimDailyBonus();
  }

  function handleCardAction(
    e: MouseEvent,
    targetServer: GameServer,
    targetAction: "start" | "stop",
  ) {
    e.preventDefault();
    e.stopPropagation();
    void runServerAction(targetServer, targetAction);
  }
</script>

<!-- Greeting hero -->
<header class="ov-hero">
  <div class="ov-hero-text">
    <p class="eyebrow">Панель управления</p>
    <h1>{getGreeting()}, <em>{$user?.username ?? "пилот"}</em></h1>
  </div>
  <a class="button" href={routeHref("server-create")}
    >Новый сервер <span aria-hidden="true">+</span></a
  >
</header>

<!-- Quick stats -->
<div class="ov-stats" role="list">
  <article class="ov-stat ov-stat--balance" role="listitem">
    <span class="ov-stat-label">Баланс</span>
    <strong class="ov-stat-value">{formatNumber($balance)}</strong>
    <span class="ov-stat-unit">кредитов · баланс для оплаты серверов</span>
    {#if runningSummary.hourlyCost > 0 && runningSummary.hoursRemaining !== null}
      <span class="ov-stat-note">
        −{runningSummary.hourlyCost} кр./ч · ~{runningSummary.hoursRemaining} ч.
      </span>
    {:else}
      <span class="ov-stat-note ov-stat-note--idle">Расход: 0 кр./ч</span>
    {/if}
  </article>

  <article class="ov-stat" role="listitem">
    <span class="ov-stat-label">Серверы</span>
    <strong class="ov-stat-value">{$servers.length}<small> / {$serverLimit}</small></strong>
    <span class="ov-stat-unit"
      >{runningSummary.count > 0 ? `${runningSummary.count} в сети` : "все остановлены"}</span
    >
  </article>

  <article class="ov-stat ov-stat--bonus" role="listitem">
    <span class="ov-stat-label">Суточный бонус</span>
    {#if $dailyBonusClaimedToday}
      <span class="ov-bonus-done">✓ Получен сегодня</span>
      <span class="ov-stat-unit">Следующий — завтра</span>
    {:else}
      <button
        class="ov-bonus-btn"
        type="button"
        disabled={$busyAction === "daily-bonus"}
        onclick={handleClaimBonus}
      >
        {$busyAction === "daily-bonus" ? "Получаем…" : "Забрать +15 кр."}
      </button>
      <span class="ov-stat-unit">Доступен прямо сейчас</span>
    {/if}
  </article>
</div>

<!-- Server cards -->
<section class="ov-servers" aria-labelledby="ov-servers-title">
  <div class="ov-servers-head">
    <h2 id="ov-servers-title">Твои серверы</h2>
    {#if $servers.length > 0}
      <a class="ov-all-link" href={routeHref("servers")}>Управление →</a>
    {/if}
  </div>

  {#if $servers.length === 0}
    <div class="ov-empty">
      <div class="ov-empty-orbit" aria-hidden="true"><i></i></div>
      <h3>Орбита пуста</h3>
      <p>Создай первый сервер и пригласи друзей.</p>
      <a class="button" href={routeHref("server-create")}>Создать сервер</a>
    </div>
  {:else}
    <div class="ov-grid">
      {#each $servers as server (server.id)}
        {@const running = server.status === "running" || server.status === "starting"}
        {@const transitioning = transitionalStatuses.has(server.status)}
        {@const action = running ? "stop" : "start"}
        {@const billingPending = isBillingSettlementPending(server)}
        {@const actionBusy = $busyAction === `${action}:${server.id}`}
        <article class="ov-card {server.status}">
          <span class="ov-card-status-bar"></span>
          <a
            class="ov-card-main"
            href={routeHref("server-home", { id: server.id })}
            aria-label={`Открыть сервер ${server.name}`}
          >
            <span class="ov-card-top">
              <span
                class={`game-symbol ${server.game.startsWith("minecraft") ? "cube-symbol" : "tree-symbol"}`}
                aria-hidden="true"
              ></span>
              <span class={`ov-card-badge ${server.status}`}>
                <i aria-hidden="true"></i>{statusLabels[server.status] ?? server.status}
              </span>
            </span>
            <span class="ov-card-name">{server.name}</span>
            <span class="ov-card-meta"
              >{gameLabel(server.game)} · {server.core} · {server.version}</span
            >
            {#if transitionalStatuses.has(server.status) || server.status === "failed"}
              <span class="ov-card-meta"
                >{serverStatusHint(server.status, server.queuePosition)}</span
              >
            {/if}
          </a>
          <div class="ov-card-footer">
            <button
              class={`ov-action-btn ${running ? "stop" : "play"}`}
              type="button"
              disabled={transitioning || billingPending || actionBusy}
              onclick={(e) => handleCardAction(e, server, action)}
              aria-label={running ? `Остановить ${server.name}` : `Запустить ${server.name}`}
            >
              {billingPending ? "Расчёт…" : actionBusy ? "…" : running ? "■ Стоп" : "▶ Запуск"}
            </button>
          </div>
        </article>
      {/each}
    </div>
  {/if}
</section>

<style>
  /* Hero */
  .ov-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 2rem;
    margin-bottom: 2rem;
  }

  .ov-hero h1 {
    margin: 0.6rem 0 0;
    font: 500 clamp(1.6rem, 4vw, 3rem)/1.1 var(--font-display);
    letter-spacing: -0.05em;
    text-transform: uppercase;
  }

  .ov-hero h1 em {
    font-style: normal;
    color: var(--mars-light);
  }

  /* Stats strip */
  .ov-stats {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr;
    gap: 1px;
    margin-bottom: 2.5rem;
    border: 1px solid var(--line);
    background: var(--line);
  }

  .ov-stat {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 1.25rem 1.4rem;
    background: var(--surface);
  }

  .ov-stat--balance {
    background: linear-gradient(145deg, rgb(214 75 50 / 0.1) 0%, var(--surface) 55%);
  }

  .ov-stat-label {
    color: var(--dim);
    font: 600 0.65rem/1 var(--font-display);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .ov-stat-value {
    color: var(--white);
    font: 500 clamp(1.6rem, 3vw, 2.2rem)/1 var(--font-display);
    letter-spacing: -0.04em;
  }

  .ov-stat-value small {
    color: var(--dim);
    font-size: 0.85rem;
  }

  .ov-stat-unit {
    color: var(--muted);
    font-size: 0.7rem;
  }

  .ov-stat-note {
    color: var(--mars-light);
    font-size: 0.7rem;
  }

  .ov-stat-note--idle {
    color: var(--dim);
  }

  .ov-bonus-done {
    color: var(--green);
    font: 600 0.82rem/1 var(--font-display);
    margin-top: 0.2rem;
  }

  .ov-bonus-btn {
    align-self: flex-start;
    margin-top: 0.2rem;
    padding: 0.5rem 1rem;
    border: 1px solid var(--action);
    color: var(--white);
    background: var(--action);
    font: 600 0.8rem/1.35 var(--font-display);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    cursor: pointer;
    transition:
      background 160ms ease,
      transform 160ms ease;
  }

  .ov-bonus-btn:hover:not(:disabled) {
    background: var(--action-hover);
    transform: translateY(-1px);
  }

  /* Servers section */
  .ov-servers {
    margin-top: 0.5rem;
  }

  .ov-servers-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  .ov-servers-head h2 {
    margin: 0;
    font: 500 clamp(1.1rem, 2.5vw, 1.5rem)/1 var(--font-display);
    letter-spacing: -0.03em;
    text-transform: uppercase;
  }

  .ov-all-link {
    color: var(--mars-light);
    text-decoration: none;
    font-size: 0.78rem;
    font-weight: 500;
    transition: color 140ms ease;
  }

  .ov-all-link:hover {
    color: var(--cream);
  }

  /* Empty state */
  .ov-empty {
    display: grid;
    justify-items: center;
    gap: 1rem;
    padding: 4rem 2rem;
    border: 1px dashed var(--line-strong);
    text-align: center;
  }

  .ov-empty h3 {
    margin: 0;
    font: 500 1.3rem/1 var(--font-display);
    text-transform: uppercase;
  }

  .ov-empty p {
    margin: 0;
    color: var(--muted);
  }

  .ov-empty-orbit {
    position: relative;
    width: 3rem;
    aspect-ratio: 1;
    border: 1px dashed var(--mars-light);
    border-radius: 50%;
    animation: spin 5s linear infinite;
  }

  .ov-empty-orbit i {
    position: absolute;
    inset: 30%;
    border-radius: 50%;
    background: var(--mars);
    box-shadow: 0 0 14px rgb(214 75 50 / 0.5);
  }

  /* Server cards grid */
  .ov-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
    gap: 1px;
    border: 1px solid var(--line);
    background: var(--line);
  }

  .ov-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    position: relative;
    padding: 1.25rem 1.35rem;
    background: var(--surface);
    color: inherit;
    text-decoration: none;
    transition: background 160ms ease;
    overflow: hidden;
  }

  .ov-card:hover {
    background: var(--surface-raised);
  }

  .ov-card-main {
    display: grid;
    flex: 1;
    gap: 0.5rem;
    color: inherit;
    text-decoration: none;
  }
  .ov-card-main:focus-visible {
    outline: 2px solid var(--mars-light);
    outline-offset: 4px;
  }

  .ov-card-status-bar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--dim);
    transition: background 200ms ease;
  }

  .ov-card.running .ov-card-status-bar {
    background: var(--green);
    box-shadow: 0 0 8px var(--green);
  }
  .ov-card.starting .ov-card-status-bar,
  .ov-card.queued_start .ov-card-status-bar {
    background: var(--amber);
  }
  .ov-card.failed .ov-card-status-bar {
    background: var(--mars);
  }
  .ov-card.installing .ov-card-status-bar,
  .ov-card.queued_install .ov-card-status-bar {
    background: var(--amber);
    animation: blink 1.2s infinite;
  }

  .ov-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .ov-card-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .ov-card-badge.running {
    color: var(--green);
  }
  .ov-card-badge.stopped {
    color: var(--dim);
  }
  .ov-card-badge.failed {
    color: var(--mars-light);
  }
  .ov-card-badge.starting,
  .ov-card-badge.queued_start,
  .ov-card-badge.queued_install,
  .ov-card-badge.queued_stop,
  .ov-card-badge.queued_delete,
  .ov-card-badge.installing,
  .ov-card-badge.stopping,
  .ov-card-badge.deleting {
    color: var(--amber);
  }

  .ov-card-badge i {
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }

  .ov-card-name {
    font: 600 1rem/1.2 var(--font-display);
    letter-spacing: -0.02em;
    color: var(--white);
  }

  .ov-card-meta {
    color: var(--muted);
    font-size: 0.72rem;
  }

  .ov-card-footer {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: auto;
    padding-top: 0.75rem;
  }

  .ov-action-btn {
    padding: 0.4rem 0.85rem;
    border: 1px solid var(--line-strong);
    color: var(--muted);
    background: transparent;
    font: 600 0.65rem/1 var(--font-display);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition:
      border-color 160ms ease,
      color 160ms ease,
      background 160ms ease;
  }

  .ov-action-btn:hover:not(:disabled) {
    border-color: var(--mars-light);
    color: var(--mars-light);
    background: rgb(214 75 50 / 0.06);
  }

  .ov-action-btn.play {
    color: var(--green);
    border-color: rgb(154 189 143 / 0.35);
  }
  .ov-action-btn.play:hover:not(:disabled) {
    border-color: var(--green);
    background: rgb(154 189 143 / 0.08);
  }
  .ov-action-btn.stop {
    color: var(--mars-light);
    border-color: rgb(214 75 50 / 0.3);
  }

  /* Responsive */
  @media (max-width: 700px) {
    .ov-hero {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }
    .ov-hero .button {
      width: 100%;
      text-align: center;
    }
    .ov-stats {
      grid-template-columns: 1fr 1fr;
    }
    .ov-stat--balance {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 520px) {
    .ov-grid {
      grid-template-columns: 1fr;
    }
    .ov-stats {
      grid-template-columns: 1fr;
    }
    .ov-stat--balance {
      grid-column: auto;
    }
    .ov-stat {
      padding: 1rem;
    }
  }
</style>
